import {useEffect, useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {App as AntdApp, Alert, Table, Tag, Tooltip} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon} from "~/components";
import {InputField, TextAreaField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useAddKeywordItemsMutation, useGetKeywordItemsQuery, useGoogleKeywordIdeasMutation} from "~/reduxs/api/keywordApiSlice";
import {useGetProjectQuery} from "~/reduxs/api/projectApiSlice";
import {metricsColumns} from "./keywordMetrics";
import style from "../style/Keyword.module.scss";

const cn = className.bind(style);

/** Trần seed nhập tay (khớp GIDEAS_MAX_SEEDS phía BE). */
const MAX_SEEDS = 20;

/**
 * Tab "Gợi ý Google": nhập từ khoá hạt giống (mỗi dòng 1 từ) và/hoặc URL → Google Keyword
 * Planner (GenerateKeywordIdeas) trả danh sách từ khoá MỚI kèm Volume/CPC/competition THẬT
 * (khác tab Ý tưởng AI: AI chỉ sinh ý tưởng, không có volume). Metrics được BE ghi luôn vào
 * cache chung nên các tab khác (Khám phá/Kho) cũng thấy. Tốn 1 lượt tra volume / lần gợi ý.
 * Chọn nhiều dòng → lưu Kho từ khoá (nguồn `google_ads`).
 */
function KeywordGoogleIdeas({projectId}) {

	const {notification} = AntdApp.useApp();
	const [generate, {isLoading: generating}] = useGoogleKeywordIdeasMutation();
	const [addItems, {isLoading: saving}] = useAddKeywordItemsMutation();

	// Dữ liệu dự án: URL website (seed URL cho Google) + kho từ khoá (seed hạt giống sẵn có).
	const {data: project} = useGetProjectQuery(projectId);
	const {data: vault} = useGetKeywordItemsQuery(projectId);
	const vaultItems = vault?.items || [];

	const [result, setResult] = useState(null);
	const [selectedKeys, setSelectedKeys] = useState([]);
	// Từ khoá đã lưu SAU khi có kết quả (server chỉ trả cờ saved tại thời điểm gợi ý).
	const [savedLocal, setSavedLocal] = useState(() => new Set());

	const {control, handleSubmit, setValue, getValues, formState: {errors}} = useForm({
		defaultValues: {seeds: '', url: ''},
	});

	// Prefill ô URL bằng website của dự án (chỉ khi user chưa gõ gì) — Google phân tích nội
	// dung trang này để gợi ý. Đây là điểm "tận dụng dữ liệu dự án" quan trọng nhất.
	useEffect(() => {
		if (project?.root_url && !getValues('url')) {
			setValue('url', project.root_url);
		}
	}, [project?.root_url, getValues, setValue]);

	// Nạp từ khoá đã có trong Kho làm seed (cộng dồn, dedup, tôn trọng trần MAX_SEEDS).
	const fillFromVault = () => {
		if (!vaultItems.length) return;
		const current = (getValues('seeds') || '').split('\n').map((s) => s.trim()).filter(Boolean);
		const merged = [...new Set([...current, ...vaultItems.map((it) => it.keyword)])].slice(0, MAX_SEEDS);
		setValue('seeds', merged.join('\n'));
	};

	const onGenerate = async ({seeds, url}) => {
		const keywords = [...new Set((seeds || '').split('\n').map((s) => s.trim()).filter(Boolean))].slice(0, MAX_SEEDS);
		const seedUrl = (url || '').trim();

		if (!keywords.length && !seedUrl) {
			notification.warning({message: 'Thiếu dữ liệu', description: 'Nhập ít nhất một từ khoá hạt giống hoặc URL.'});
			return;
		}

		try {
			const data = await generate({projectId, keywords, url: seedUrl}).unwrap();

			if (data && !data.available) {
				notification.warning({
					message: 'Chưa sẵn sàng',
					description: 'Hệ thống chưa kết nối Google Ads — liên hệ quản trị viên.',
				});
				return;
			}

			setResult(data);
			setSelectedKeys([]);
			setSavedLocal(new Set());

			if (!data?.rows?.length) {
				notification.warning({message: 'Không có kết quả', description: 'Google không trả về gợi ý nào — thử seed khác.'});
			}
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lấy được gợi ý từ Google')});
		}
	};

	const isSaved = (r) => r.saved || savedLocal.has(r.keyword);

	const save = async (rows) => {
		const list = rows.filter((r) => !isSaved(r));
		if (!list.length) return;
		try {
			const res = await addItems({
				projectId,
				items: list.map((r) => ({
					keyword: r.keyword,
					source: 'google_ads',
					...(r.gsc ? {impressions: r.gsc.impressions, position: r.gsc.position} : {}),
					meta: {seed: (result?.seed?.keywords || []).join(', ') || result?.seed?.url || ''},
				})),
			}).unwrap();
			setSavedLocal((prev) => {
				const next = new Set(prev);
				list.forEach((r) => next.add(r.keyword));
				return next;
			});
			setSelectedKeys([]);
			notification.success({message: 'Đã lưu vào kho', description: `${res?.added ?? list.length} từ khoá mới${res?.skipped ? ` (${res.skipped} đã có sẵn)` : ''}.`});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được từ khoá')});
		}
	};

	const rows = result?.rows || [];

	const columns = [
		{
			title: 'Từ khoá', dataIndex: 'keyword', key: 'keyword',
			render: (v, r) => (
				<span>
					{v}
					{isSaved(r) && <FontAwesomeIcon icon="fa-solid fa-circle-check" style={{color: '#16a34a', marginLeft: 6}} />}
				</span>
			),
		},
		// Volume / CPC / KD dùng chung 3 cột chuẩn (metrics BE trả sẵn trong row — không poll).
		...metricsColumns((r) => ({metrics: r.metrics, pending: false})),
		{
			title: <Tooltip title="Số liệu thật từ Search Console nếu site đã hiển thị với từ này">GSC</Tooltip>,
			key: 'gsc', align: 'center', width: 170, responsive: ['md'],
			sorter: (a, b) => (a.gsc?.impressions ?? -1) - (b.gsc?.impressions ?? -1),
			render: (_, r) => r.gsc
				? (
					<Tooltip title={`${r.gsc.clicks} clicks · CTR ${(r.gsc.ctr * 100).toFixed(1)}% (${r.gsc.from} → ${r.gsc.to})`}>
						<Tag color="green">{r.gsc.impressions.toLocaleString('vi-VN')} impr · vị trí {Number(r.gsc.position).toFixed(1)}</Tag>
					</Tooltip>
				)
				: <span style={{color: '#9ca3af'}}>—</span>,
		},
		{
			title: '', key: 'actions', align: 'right', width: 60,
			render: (_, r) => (
				<Button small white disabled={isSaved(r) || saving} onClick={() => save([r])}
					leftIcon={<FontAwesomeIcon icon="fa-light fa-bookmark" />} />
			),
		},
	];

	return (
		<div>
			<Alert type="info" showIcon style={{marginBottom: 14}}
				message="Gợi ý từ khoá MỚI từ Google Keyword Planner kèm Volume/CPC thật (khác tab Ý tưởng AI — AI không có volume). Mỗi lần gợi ý tính 1 lượt tra volume của gói." />

			{/* Scope class toàn cục `form` để các Field (.form-group/.form-control) ăn style chuẩn của site. */}
			<div className={cn('section') + ' form'}>
				<h3 className={cn('sectionTitle')}>Lấy gợi ý từ Google</h3>
				<Controller control={control} name="seeds" render={({field}) => (
					<TextAreaField label={`Từ khoá hạt giống (mỗi dòng 1 từ, tối đa ${MAX_SEEDS})`}
						placeholder={'vd:\ndịch vụ seo\nseo website'} rows={4} errors={errors} {...field} />
				)} />
				{vaultItems.length > 0 && (
					<Button small white style={{marginBottom: 12}} onClick={fillFromVault}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-tags" />}>
						Nạp {Math.min(vaultItems.length, MAX_SEEDS)} từ khoá từ Kho làm seed
					</Button>
				)}
				<Controller control={control} name="url" render={({field}) => (
					<InputField label="Hoặc URL trang/website (Google phân tích nội dung để gợi ý)"
						placeholder="vd: https://example.com/dich-vu-seo" errors={errors} {...field} />
				)} />
				<Button primary loading={generating} onClick={handleSubmit(onGenerate)}
					leftIcon={<FontAwesomeIcon icon="fa-brands fa-google" />}>
					Lấy gợi ý
				</Button>
				{result?.quota && (
					<span className={cn('hint')} style={{marginLeft: 12}}>
						Đã dùng {result.quota.used}/{result.quota.limit || '∞'} lượt tra trong tháng
					</span>
				)}
			</div>

			{rows.length > 0 && (
				<div className={cn('section')}>
					<div className={cn('actionBar')} style={{marginBottom: 10}}>
						<h3 className={cn('sectionTitle')} style={{margin: 0}}>
							{rows.length} gợi ý từ Google
						</h3>
						<Button small primary disabled={!selectedKeys.length || saving} loading={saving}
							onClick={() => save(rows.filter((r) => selectedKeys.includes(r.hash)))}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-bookmark" />}>
							Lưu {selectedKeys.length > 0 ? `${selectedKeys.length} từ đã chọn` : 'vào kho'}
						</Button>
					</div>

					<div className="table-scroll">
						<Table
							rowKey="hash"
							size="small"
							columns={columns}
							dataSource={rows}
							pagination={{pageSize: 50, hideOnSinglePage: true, showSizeChanger: false}}
							rowSelection={{
								selectedRowKeys: selectedKeys,
								onChange: setSelectedKeys,
								getCheckboxProps: (r) => ({disabled: isSaved(r)}),
							}}
							scroll={{x: 700}}
						/>
					</div>
				</div>
			)}
		</div>
	);
}

export default KeywordGoogleIdeas;
